/**
 * Offline queue for leaderboard POSTs. Scores are stored locally when the network
 * is unavailable and flushed automatically when the app is online and signed in.
 */

import { apiPath } from "./apiPath";
import { invalidateMyBestsCache } from "./cloud";

const STORAGE_KEY = "pendingLeaderboardScores";

export type LeaderboardSubmitBody = {
  mode: string;
  value: number;
  level?: number;
  presetId?: string;
  clientVersion?: string;
  device?: "pc" | "mobile";
  /** Optional validated replay payload (not queued offline — too large). */
  replay?: unknown;
};

type PendingEntry = {
  id: string;
  body: LeaderboardSubmitBody;
  queuedAt: string;
};

function readQueue(): PendingEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is PendingEntry =>
        e != null &&
        typeof e === "object" &&
        typeof (e as PendingEntry).id === "string" &&
        (e as PendingEntry).body != null &&
        typeof (e as PendingEntry).body.mode === "string" &&
        Number.isFinite(Number((e as PendingEntry).body.value)),
    );
  } catch {
    return [];
  }
}

function writeQueue(entries: PendingEntry[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

/** Stable key to dedupe pending and already-submitted scores. */
export function leaderboardDedupeKey(body: LeaderboardSubmitBody): string {
  const level = body.level != null ? String(Math.floor(Number(body.level))) : "";
  const presetId = body.presetId != null ? String(body.presetId).trim() : "";
  return `${body.mode}|${level}|${presetId}|${Math.floor(Number(body.value))}`;
}

function newEntryId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function enqueuePendingLeaderboardScore(body: LeaderboardSubmitBody): void {
  // Never persist bulky replay blobs in localStorage.
  const { replay: _replay, ...rest } = body;
  const key = leaderboardDedupeKey(rest);
  const queue = readQueue();
  if (queue.some((e) => leaderboardDedupeKey(e.body) === key)) return;
  queue.push({ id: newEntryId(), body: rest, queuedAt: new Date().toISOString() });
  writeQueue(queue);
}

export function getPendingLeaderboardCount(): number {
  return readQueue().length;
}

async function postLeaderboard(body: LeaderboardSubmitBody): Promise<Response> {
  invalidateMyBestsCache();
  try {
    return await fetch(apiPath("/api/leaderboard"), {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } finally {
    // Also drop anything fetched while the POST was in flight.
    invalidateMyBestsCache();
  }
}

/**
 * Submit a score to the cloud; queue locally when offline or on transient errors.
 * Returns whether the score was accepted online, queued, or skipped.
 */
export async function submitLeaderboardWithOffline(
  body: LeaderboardSubmitBody,
): Promise<"ok" | "queued" | "skipped"> {
  if (!navigator.onLine) {
    enqueuePendingLeaderboardScore(body);
    return "queued";
  }
  try {
    const r = await postLeaderboard(body);
    if (r.ok) return "ok";
    if (r.status === 401 || r.status === 503 || r.status === 404 || r.status === 400) {
      return "skipped";
    }
    if (r.status >= 500) {
      enqueuePendingLeaderboardScore(body);
      return "queued";
    }
    return "skipped";
  } catch {
    enqueuePendingLeaderboardScore(body);
    return "queued";
  }
}

let flushInFlight: Promise<number> | null = null;

/**
 * Send all pending scores. Requires network; stops on auth failure or network error.
 * Returns the number of successfully submitted entries.
 */
export async function flushPendingLeaderboardScores(): Promise<number> {
  if (flushInFlight) return flushInFlight;
  flushInFlight = (async () => {
    if (!navigator.onLine) return 0;
    const queue = readQueue();
    if (!queue.length) return 0;

    const submittedKeys = new Set<string>();
    let sent = 0;
    const remaining: PendingEntry[] = [];

    for (const entry of queue) {
      const key = leaderboardDedupeKey(entry.body);
      if (submittedKeys.has(key)) continue;

      try {
        const r = await postLeaderboard(entry.body);
        if (r.ok) {
          submittedKeys.add(key);
          sent += 1;
          continue;
        }
        if (r.status === 401) {
          remaining.push(entry, ...queue.slice(queue.indexOf(entry) + 1));
          break;
        }
        if (r.status === 503 || r.status === 404 || r.status === 400) {
          continue;
        }
        if (r.status >= 500) {
          remaining.push(entry, ...queue.slice(queue.indexOf(entry) + 1));
          break;
        }
      } catch {
        remaining.push(entry, ...queue.slice(queue.indexOf(entry) + 1));
        break;
      }
    }

    writeQueue(remaining);
    return sent;
  })().finally(() => {
    flushInFlight = null;
  });

  return flushInFlight;
}

export function initPendingLeaderboardSync(onSignedIn: () => void): void {
  const tryFlush = () => {
    void flushPendingLeaderboardScores().then((n) => {
      if (n > 0) onSignedIn();
    });
  };

  window.addEventListener("online", tryFlush);
  if (navigator.onLine) tryFlush();
}
